import * as React from 'react';
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    List,
    ListRowProps
} from 'react-virtualized';

import { Bem } from '../../service/Bem/Bem';
import {
    OutdoorMapPlatform,
    OutdoorMapRegionPlatform
} from '../OutdoorMap/OutdoorMap';
import { OutdoorPlatform } from '../OutdoorPlatform/OutdoorPlatform';
import { OutdoorListItem } from '../OutdoorPlatformList/OutdoorPlatformList';
import { OutdoorRegionHeader } from '../OutdoorRegionHeader/OutdoorRegionHeader';

import './OutdoorVirtualList.css';

export interface OutdoorMapRegionPlatform {
    type: string;
    regionId: string;
    regionText: string;
    regionCenter: [number, number];
}

export type listItem = OutdoorMapRegionPlatform | OutdoorMapPlatform;

export interface OutdoorVirtualListProps {
    checkedItems: string[];
    list: listItem[];
    openedRegions: {
        [id: string]: boolean;
    };
    onRegionArrowClick: (data: { regionId: string; isOpen: boolean }) => void;
    onRegionCheckBoxChange: (data: {
        regionId: string;
        checked: boolean;
        itemIds: string[];
        items: OutdoorMapPlatform[];
    }) => void;
    scrollToId?: string;
    highlightId?: string;
    onItemChecked: (platform: listItem) => void;
    onAddressClick?: (platform: listItem) => void;
    search: {
        value: string;
        test: RegExp;
        match: RegExp;
    };
    virtualList: (OutdoorMapRegionPlatform | OutdoorMapPlatform)[];
}

export class OutdoorVirtualList extends React.Component<
    OutdoorVirtualListProps
> {
    private List: List | null;
    private cache: CellMeasurerCache;

    constructor(props: OutdoorVirtualListProps) {
        super(props);

        this.cache = new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 115
        });
    }

    componentDidUpdate() {
        const { scrollToId } = this.props;

        if (this.List && scrollToId) {
            const { list, openedRegions } = this.props;
            const regionsId = Object.keys(openedRegions).filter(
                keyVal => openedRegions[keyVal]
            );
            const regionsList = this.getRegionsList(list, regionsId, true);

            let scrollToIndex: number | undefined;

            for (let index = 0; index < regionsList.length; index++) {
                const item = regionsList[index] as OutdoorMapPlatform;
                if (
                    scrollToId === item.id ||
                    (scrollToId === item.regionId && item.type === 'region')
                ) {
                    scrollToIndex = index;
                    break;
                }
            }

            this.List.scrollToRow(scrollToIndex);
        }
    }

    render() {
        const { list, openedRegions } = this.props;
        const regionsId = Object.keys(openedRegions).filter(
            keyVal => openedRegions[keyVal]
        );
        const regionsList = this.getRegionsList(list, regionsId, true);
        this.cache.clearAll();

        return (
            <div className={Bem.cls('outdoor-virtual-list')}>
                <AutoSizer>
                    {({ width, height }) => {
                        return (
                            <List
                                ref={ref => (this.List = ref)}
                                width={width}
                                height={height}
                                className={Bem.cls(
                                    'outdoor-virtual-list',
                                    'grid'
                                )}
                                deferredMeasurementCache={this.cache}
                                rowHeight={index =>
                                    this.cache.rowHeight(index) || 0
                                }
                                rowRenderer={this.renderRow}
                                rowCount={regionsList.length}
                                overscanRowCount={3}
                                scrollToAlignment={'start'}
                            />
                        );
                    }}
                </AutoSizer>
            </div>
        );
    }

    private renderRow = ({
        index,
        key,
        style,
        parent
    }: ListRowProps & { parent: React.ReactType }) => {
        const { list, openedRegions } = this.props;
        const regionsId = Object.keys(openedRegions).filter(
            keyVal => openedRegions[keyVal]
        );
        const regionsList = this.getRegionsList(list, regionsId, true);
        const item = regionsList[index] as OutdoorMapPlatform;

        if (item.type === 'region') {
            return (
                <CellMeasurer
                    key={key}
                    cache={this.cache}
                    parent={parent}
                    columnIndex={0}
                    rowIndex={index}
                >
                    <div style={style}>
                        {this.getRegionHeader(
                            regionsList[index] as OutdoorMapRegionPlatform,
                            index
                        )}
                    </div>
                </CellMeasurer>
            );
        } else {
            return (
                <CellMeasurer
                    key={key}
                    cache={this.cache}
                    parent={parent}
                    columnIndex={0}
                    rowIndex={index}
                >
                    <div style={style}>{this.getPlatfrom(item)}</div>
                </CellMeasurer>
            );
        }
    };

    private getRegionsList = (
        source: (listItem | OutdoorListItem)[],
        regionId: string[],
        regionHeader: boolean = false
    ) => {
        return source.filter(
            (item: OutdoorMapPlatform) =>
                (item.type !== 'region' && regionId.includes(item.regionId)) ||
                (regionHeader && item.type === 'region')
        );
    };

    private getRegionsCheckedItems = (regionId: string) => {
        const { list, checkedItems } = this.props;
        const regionList = this.getRegionsList(list, [regionId], false).map(
            (item: OutdoorMapPlatform) => item.id
        );

        return checkedItems.filter(id => regionList.includes(id));
    };

    private getRegionHeader = (
        item: OutdoorMapRegionPlatform,
        index: number
    ) => {
        const {
            list,
            search,
            openedRegions,
            onRegionArrowClick,
            virtualList
        } = this.props;
        const foundCount = this.getRegionsList(list, [item.regionId]).length;

        return (
            <OutdoorRegionHeader
                checked={
                    !!foundCount
                        ? this.isRegionChecked(item.regionId, index)
                        : false
                }
                totalCount={
                    this.getRegionsList(virtualList, [item.regionId]).length
                }
                selectedCount={
                    this.getRegionsCheckedItems(item.regionId).length
                }
                regionText={item.regionText}
                regionId={item.regionId}
                isOpen={openedRegions[item.regionId]}
                foundCount={foundCount}
                search={search}
                onArrowClick={onRegionArrowClick}
                onCheckBoxChange={this.onRegionCheckBoxChange}
            />
        );
    };

    private getPlatfrom = (item: OutdoorMapPlatform) => {
        const {
            search,
            onItemChecked,
            checkedItems,
            onAddressClick,
            scrollToId,
            highlightId
        } = this.props;

        return (
            <OutdoorPlatform
                platform={item}
                highlighted={item.id === scrollToId || highlightId === item.id}
                imageLink={
                    'https://w-dog.net/wallpapers/6/2/326939737322301.jpg'
                }
                search={search}
                checked={checkedItems.includes(item.id)}
                onChange={onItemChecked}
                onAddressClick={onAddressClick}
            />
        );
    };

    private onRegionCheckBoxChange = (data: {
        regionId: string;
        checked: boolean;
    }) => {
        const { list, onRegionCheckBoxChange } = this.props;
        const items: OutdoorMapPlatform[] = [];
        const regionItemIds = list.reduce(
            (result: string[], item: OutdoorMapPlatform) => {
                if (
                    item.type === 'platform' &&
                    item.regionId === data.regionId
                ) {
                    items.push(item);
                    result.push(item.id);
                }

                return result;
            },
            []
        );

        onRegionCheckBoxChange({
            ...data,
            itemIds: regionItemIds,
            items: items
        });
    };

    private isRegionChecked = (regionId: string, index: number) => {
        const { list, checkedItems } = this.props;
        let item: OutdoorMapPlatform | OutdoorMapRegionPlatform;

        for (let i = 0; i < list.length; i++) {
            item = list[i] as OutdoorMapPlatform;

            if (item.type === 'region' && item.regionId === regionId) {
                index = i;
                break;
            }
        }

        let isChecked = true;

        for (index += 1; index < list.length; index++) {
            item = list[index] as OutdoorMapPlatform;

            if (item.regionId !== regionId) {
                break;
            }

            if (!checkedItems.includes(item.id)) {
                isChecked = false;
                break;
            }
        }

        return isChecked;
    };
}
